import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../lib/store';
import { getToken } from '../services/api';
import './marketing.css';

const WHATSAPP_URL =
  "https://wa.me/919632815100?text=Hi%21%20I%27d%20love%20to%20know%20more%20about%20The%20Good%20F%C3%BCd%27s%20meal%20subscription%20plans.%20Could%20you%20please%20share%20some%20details%3F";

/**
 * Shared chrome for all public marketing pages: top nav (with Login), mobile
 * nav overlay, footer, contact modal, floating WhatsApp button, and the
 * scroll-reveal animation observer. Everything is wrapped in `.tgf` so the
 * scoped marketing stylesheet never leaks into the app.
 */
export const MarketingLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);

  const { isAuthenticated } = useSelector((s: RootState) => s.auth);
  const loggedIn = isAuthenticated || !!getToken();

  // We always force scroll-to-top on route change below, so hand scroll
  // restoration off to the browser's default (which otherwise fights this
  // on back/forward navigation, causing a visible jump).
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Close menus + scroll to top on route change
  useEffect(() => {
    setMobileOpen(false);
    setContactOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Lock body scroll while an overlay is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen || contactOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen, contactOpen]);

  // Scroll-reveal: add `.visible` to `.fade-in-up` elements as they enter view
  useEffect(() => {
    const els = document.querySelectorAll('.tgf .fade-in-up:not(.visible)');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [location.pathname]);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactStatus('idle');
    try {
      const res = await fetch('https://formsubmit.co/ajax/reachus@thegoodfud.com', {
        method: 'POST',
        body: new FormData(e.currentTarget),
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        setContactStatus('success');
        formRef.current?.reset();
        setTimeout(() => {
          setContactOpen(false);
          setContactStatus('idle');
        }, 3000);
      } else {
        setContactStatus('error');
      }
    } catch {
      setContactStatus('error');
    }
  };

  // Delegated handling for links & modal triggers inside injected page content.
  const setDeliveryModal = (open: boolean) => {
    const m = document.getElementById('delivery-modal');
    if (!m) return;
    m.classList.toggle('active', open);
    document.body.style.overflow = open ? 'hidden' : '';
    if (!open) {
      const form = document.getElementById('delivery-form') as HTMLFormElement | null;
      form?.reset();
      const c = document.getElementById('delivery-form-content');
      const s = document.getElementById('delivery-success');
      const f = document.getElementById('delivery-failure');
      if (c) c.style.display = 'block';
      if (s) s.style.display = 'none';
      if (f) f.style.display = 'none';
    }
  };

  const handleRootClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.defaultPrevented) return; // already handled by a React <Link>
    const target = e.target as HTMLElement;
    if (target.closest('.open-contact-modal')) {
      e.preventDefault();
      setContactOpen(true);
      return;
    }
    if (target.closest('.open-delivery')) {
      e.preventDefault();
      setDeliveryModal(true);
      return;
    }
    if (target.closest('.close-modal, .close-modal-btn')) {
      e.preventDefault();
      setDeliveryModal(false);
      return;
    }
    // Click on the delivery modal backdrop closes it
    if (target.id === 'delivery-modal') {
      setDeliveryModal(false);
      return;
    }
    const a = target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (
      a.getAttribute('target') === '_blank' ||
      /^(https?:|mailto:|tel:|#)/.test(href) ||
      href === ''
    ) {
      return; // external / anchor — default behaviour
    }
    if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
    }
  };

  const handleRootSubmit = (e: React.FormEvent<HTMLDivElement>) => {
    const form = e.target as HTMLElement;
    if (form.id !== 'delivery-form') return;
    e.preventDefault();
    const pincode = (document.getElementById('pincode') as HTMLInputElement | null)?.value?.trim();
    const c = document.getElementById('delivery-form-content');
    const s = document.getElementById('delivery-success');
    const f = document.getElementById('delivery-failure');
    if (c) c.style.display = 'none';
    if (pincode === '560061') {
      if (s) s.style.display = 'block';
    } else if (f) {
      f.style.display = 'block';
    }
  };

  return (
    <div className="tgf" onClick={handleRootClick} onSubmit={handleRootSubmit}>
      {/* Nav */}
      <nav className="navbar">
        <div className="container nav-container">
          <Link to="/" className="logo">
            <img src="/marketing/assets/logo.png" alt="The Good Food Way" />
          </Link>
          <div className="nav-links">
            <NavLink to="/" end>Home</NavLink>
            <NavLink to="/meal-plans">Meal Plans</NavLink>
            <NavLink to="/about">About Us</NavLink>
            <NavLink to="/blog">Blogs</NavLink>
            <NavLink to="/get-started">Check Delivery</NavLink>
          </div>
          <div className="desktop-only" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ padding: '0.5rem 1.5rem', fontSize: '0.85rem' }}
              onClick={() => setContactOpen(true)}
            >
              Contact
            </button>
            <Link
              to={loggedIn ? '/dashboard' : '/login'}
              className="btn btn-primary"
              style={{ padding: '0.5rem 1.5rem', fontSize: '0.85rem' }}
            >
              {loggedIn ? 'Dashboard' : 'Login'}
            </Link>
          </div>
          <button className="mobile-toggle" aria-label="Toggle Menu" onClick={() => setMobileOpen(true)}>
            <i className="fas fa-bars" />
          </button>
        </div>
      </nav>

      {/* Mobile Navigation Overlay */}
      <div className={`mobile-nav${mobileOpen ? ' active' : ''}`}>
        <button className="close-mobile-nav" aria-label="Close Menu" onClick={() => setMobileOpen(false)}>
          <i className="fas fa-times" />
        </button>
        <div
          className="mobile-nav-logo"
          style={{
            marginBottom: '2rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            width: '100%',
          }}
        >
          <img src="/marketing/assets/logo.png" alt="The Good Food Way" style={{ height: 50, width: 'auto' }} />
        </div>
        <div className="mobile-nav-links">
          <NavLink to="/" end>Home</NavLink>
          <NavLink to="/meal-plans">Meal Plans</NavLink>
          <NavLink to="/about">About Us</NavLink>
          <NavLink to="/blog">Blogs</NavLink>
          <NavLink to="/get-started">Check Delivery</NavLink>
          <button
            type="button"
            className="mobile-link-btn"
            onClick={() => {
              setMobileOpen(false);
              setContactOpen(true);
            }}
          >
            Contact Us
          </button>
          <Link
            to={loggedIn ? '/dashboard' : '/login'}
            className="btn btn-primary"
            style={{ marginTop: '1rem', textAlign: 'center', justifyContent: 'center' }}
          >
            {loggedIn ? 'Go to Dashboard' : 'Login'}
          </Link>
          <a
            href={WHATSAPP_URL}
            className="btn btn-secondary"
            target="_blank"
            rel="noreferrer"
            style={{ marginTop: '1rem', textAlign: 'center', justifyContent: 'center' }}
          >
            WhatsApp Us
          </a>
        </div>
      </div>

      {/* Page content */}
      <Outlet context={{ openContact: () => setContactOpen(true) }} />

      {/* Footer */}
      <footer style={{ marginTop: '4rem' }}>
        <div
          className="container"
          style={{
            paddingBottom: '2rem',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            marginBottom: '2rem',
          }}
        >
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', textAlign: 'center', fontWeight: 500 }}>
            Fresh, nutritionist-planned meals designed for your body and lifestyle.
          </p>
        </div>
        <div className="footer-grid">
          <div className="footer-brand">
            <Link to="/">
              <img src="/marketing/assets/logo.png" alt="The Good Food Way" className="footer-logo" />
            </Link>
            <p>
              THE GOOD FOOD WAY<br />
              <em>Food that works for you</em>
            </p>
          </div>
          <div className="footer-col">
            <h4>Company</h4>
            <ul className="footer-links">
              <li><Link to="/">Home</Link></li>
              <li><Link to="/meal-plans">Meal Plans</Link></li>
              <li><Link to="/about">About</Link></li>
              <li><Link to="/blog">Blog</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Connect</h4>
            <ul className="footer-links">
              <li><a href="mailto:reachus@thegoodfud.com"><i className="fas fa-envelope" /> reachus@thegoodfud.com</a></li>
              <li><a href="tel:+919632815100"><i className="fas fa-phone" /> +91 96328 15100</a></li>
            </ul>
            <button className="footer-contact-btn" onClick={() => setContactOpen(true)}>
              Send us a message
            </button>
            <div className="social-links" style={{ marginTop: '2rem' }}>
              <a href="https://www.instagram.com/goodfoodway?stkn=b3pqc3BpNzB4dTNz" target="_blank" rel="noreferrer" className="social-link"><i className="fab fa-instagram" /></a>
              <a href="https://www.facebook.com/share/1ECMwZ7LP6/?mibextid=wwXIfr" target="_blank" rel="noreferrer" className="social-link"><i className="fab fa-facebook-f" /></a>
              <a href="https://www.linkedin.com/company/goodfoodway/" target="_blank" rel="noreferrer" className="social-link"><i className="fab fa-linkedin-in" /></a>
              <a href="https://youtube.com/@thegoodfud" target="_blank" rel="noreferrer" className="social-link"><i className="fab fa-youtube" /></a>
              <a href="https://pin.it/43PyExrXx" target="_blank" rel="noreferrer" className="social-link"><i className="fab fa-pinterest" /></a>
            </div>
          </div>
          <div className="footer-col">
            <h4>Locations</h4>
            <h5 style={{ color: 'white', fontSize: '1rem', marginBottom: '0.5rem', fontWeight: 700 }}>India</h5>
            <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, fontSize: '0.95rem' }}>
              No. 45, C2 Gubbalala Village<br />
              (Deepika Layout), Uttarahalli Hobli,<br />
              Bangalore South, Karnataka- 560061
            </p>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <div
        className={`contact-modal-overlay${contactOpen ? ' active' : ''}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setContactOpen(false);
        }}
      >
        <div className="contact-modal-content">
          <button className="close-contact-modal" aria-label="Close" onClick={() => setContactOpen(false)}>
            &times;
          </button>
          <div className="contact-modal-header">
            <h3>Get in Touch</h3>
            <p>Send us your enquiry and we'll get back to you soon.</p>
          </div>
          <div className={`contact-success-message${contactStatus === 'success' ? ' active' : ''}`}>
            Thank you! Your message has been sent successfully.
          </div>
          <div className={`contact-error-message${contactStatus === 'error' ? ' active' : ''}`}>
            Oops! Something went wrong. Please try again.
          </div>
          <form ref={formRef} className="contact-form" onSubmit={handleContactSubmit}>
            <div className="form-group">
              <label htmlFor="contact-name">Name *</label>
              <input type="text" id="contact-name" name="name" required placeholder="Your name" />
            </div>
            <div className="form-group">
              <label htmlFor="contact-email">Email *</label>
              <input type="email" id="contact-email" name="email" required placeholder="your@email.com" />
            </div>
            <div className="form-group">
              <label htmlFor="contact-phone">Phone Number</label>
              <input type="tel" id="contact-phone" name="phone" placeholder="+91 98765 43210" />
            </div>
            <div className="form-group">
              <label htmlFor="contact-message">Message *</label>
              <textarea id="contact-message" name="message" required placeholder="Tell us about your enquiry..." />
            </div>
            <button type="submit" className="btn btn-primary btn-submit">Send Message</button>
          </form>
        </div>
      </div>

      {/* Floating WhatsApp */}
      <a href={WHATSAPP_URL} className="floating-whatsapp" target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp">
        <svg width="35" height="35" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>
    </div>
  );
};
